import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  productApi,
  SearchProductsRequest,
  SearchProductsResponse,
  Product,
  CreateProductRequest,
  UoM,
  Part,
  PurchaseOrder,
  WorkOrder,
  Serial
} from '../../services/productApi';
import {
  fetchProductsStart,
  fetchProductsSuccess,
  fetchProductsFailure,
  fetchProductByIdStart,
  fetchProductByIdSuccess,
  fetchProductByIdFailure,
  createProductStart,
  createProductSuccess,
  createProductFailure,
  updateProductStart,
  updateProductSuccess,
  updateProductFailure,
  deleteProductStart,
  deleteProductSuccess,
  deleteProductFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  setProductValidationErrors
} from '../reducers/productSlice';
import { RootState } from '../index';

// Product Sagas
function* fetchProductsApi(action: PayloadAction<SearchProductsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { productPagination, productSearchTerm, productActiveFilter } = state.product;
    
    const params: SearchProductsRequest = action.payload || {
      pageNum: productPagination.currentPage,
      pageSize: productPagination.pageSize,
      filter: {
        ...(productSearchTerm && { Name: productSearchTerm }),
        ...(productActiveFilter !== null && { Active: productActiveFilter }),
      },
    };
    
    const response: SearchProductsResponse = yield call(productApi.getAllProducts, params);
    yield put(fetchProductsSuccess(response));
  } catch (error: any) {
    yield put(fetchProductsFailure(error.message || 'Failed to fetch products'));
  }
}

function* fetchProductByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const product: Product = yield call(productApi.getProductById, action.payload);
    yield put(fetchProductByIdSuccess(product));
  } catch (error: any) {
    yield put(fetchProductByIdFailure(error.message || 'Failed to fetch product'));
  }
}

function* createProductApi(action: PayloadAction<CreateProductRequest>): Generator<any, void, any> {
  try {
    // Validate data first - cast to Partial<Product> for validation
    const validationErrors = productApi.validateProduct(action.payload as Partial<Product>);
    if (validationErrors.length > 0) {
      yield put(setProductValidationErrors(validationErrors));
      yield put(createProductFailure('Validation failed'));
      return;
    }

    const product: Product = yield call(productApi.createProduct, action.payload);
    yield put(createProductSuccess(product));
  } catch (error: any) {
    yield put(createProductFailure(error.message || 'Failed to create product'));
  }
}

function* updateProductApi(action: PayloadAction<Partial<Product>>): Generator<any, void, any> {
  try {
    // Validate data first
    const validationErrors = productApi.validateProduct(action.payload);
    if (validationErrors.length > 0) {
      yield put(setProductValidationErrors(validationErrors));
      yield put(updateProductFailure('Validation failed'));
      return;
    }

    const product: Product = yield call(productApi.updateProduct, action.payload);
    yield put(updateProductSuccess(product));
  } catch (error: any) {
    yield put(updateProductFailure(error.message || 'Failed to update product'));
  }
}

function* deleteProductApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(productApi.deleteProduct, action.payload);
    yield put(deleteProductSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteProductFailure(error.message || 'Failed to delete product'));
  }
}

function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    const [uomsResponse, partsResponse]: [UoM[], Part[]] = yield all([
      call(productApi.getActiveUoMs),
      call(productApi.getActiveParts)
    ]);
    
    yield put(fetchDropdownsSuccess({
      uoms: uomsResponse,
      parts: partsResponse
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

function* fetchPurchaseOrdersByPartApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const purchaseOrders: PurchaseOrder[] = yield call(productApi.getPurchaseOrdersByPartId, action.payload);
    yield put(fetchDropdownsSuccess({ purchaseOrders }));
  } catch (error: any) {
    console.error('Failed to fetch purchase orders:', error);
  }
}

function* fetchWorkOrdersByPOsApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    const workOrders: WorkOrder[] = yield call(productApi.getWorkOrdersByPOIds, action.payload);
    yield put(fetchDropdownsSuccess({ workOrders }));
  } catch (error: any) {
    console.error('Failed to fetch work orders:', error);
  }
}

function* fetchSerialsByWorkOrdersApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    const serials: Serial[] = yield call(productApi.getSerialsByWorkOrders, action.payload);
    yield put(fetchDropdownsSuccess({ serials }));
  } catch (error: any) {
    console.error('Failed to fetch serials:', error);
  }
}

// Watch for filter changes
function* watchFilters(): Generator<any, void, any> {
  yield takeLatest(['product/setProductSearchTerm', 'product/setProductActiveFilter'], function* () {
    yield put(fetchProductsStart());
  });
}

export default function* productSaga(): Generator<any, void, any> {
  yield all([
    takeLatest(fetchProductsStart.type, fetchProductsApi),
    takeLatest(fetchProductByIdStart.type, fetchProductByIdApi),
    takeLatest(createProductStart.type, createProductApi),
    takeLatest(updateProductStart.type, updateProductApi),
    takeLatest(deleteProductStart.type, deleteProductApi),
    takeLatest(fetchDropdownsStart.type, fetchDropdownsApi),
    takeLatest('product/fetchPurchaseOrdersByPart', fetchPurchaseOrdersByPartApi),
    takeLatest('product/fetchWorkOrdersByPOs', fetchWorkOrdersByPOsApi),
    takeLatest('product/fetchSerialsByWorkOrders', fetchSerialsByWorkOrdersApi),
    watchFilters(),
  ]);
} 